import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "components/ui/Modal";
import { Button } from "components/ui/Button";
import { Info } from "lucide-react";
import fetchWithCredentials from "apiClient";
import { useAuth } from "context/AuthContext";

type Mode = "simulated" | "live";

interface ModeSelectorProps {
  condoId: number;
  currentMode: Mode;
  unitsCount: number;
  // Another building on this account is already live, so it may go live on
  // the shared line without connecting a number.
  accountAlreadyLive: boolean;
  onModeChange: (newMode: Mode) => void;
}

export function ModeSelector({
  condoId,
  currentMode,
  unitsCount,
  accountAlreadyLive,
  onModeChange,
}: ModeSelectorProps) {
  const [showModal, setShowModal] = useState(false);
  const [selectedMode, setSelectedMode] = useState<Mode | null>(null);
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { lang, hasWappConnectedNumber, wappDisconnected, wappTokenExpired } =
    useAuth();
  const locale = lang === "es" ? "es" : "en";
  const assistantNumberNeedsReconnect =
    (hasWappConnectedNumber && wappDisconnected) || wappTokenExpired;
  const mustConnectAssistantNumber =
    !accountAlreadyLive &&
    (!hasWappConnectedNumber || assistantNumberNeedsReconnect);
  const [unitPrice, setUnitPrice] = useState(0.7);
  const totalMonthlyCharge = unitsCount * unitPrice;
  const connectNumberMessage = assistantNumberNeedsReconnect
    ? locale === "es"
      ? "El número de WhatsApp del asistente está desconectado. Vuelve a conectarlo en Configuración → Asistente antes de activar el modo en vivo."
      : "The assistant's WhatsApp number is disconnected. Reconnect it in Settings → Assistant before going live."
    : locale === "es"
      ? "Conecta un número de WhatsApp para el asistente en Configuración → Asistente antes de activar el modo en vivo."
      : "Connect a WhatsApp number for the assistant in Settings → Assistant before going live.";
  const connectNumberAction = assistantNumberNeedsReconnect
    ? locale === "es"
      ? "Reconectar"
      : "Reconnect"
    : locale === "es"
      ? "Conectar número"
      : "Connect number";

  useEffect(() => {
    let cancelled = false;

    const fetchSubscriptionPrice = async () => {
      try {
        const data = await fetchWithCredentials(
          "/manager-api/property-manager/settings/"
        );
        if (
          !cancelled &&
          typeof data?.subscriptionPrice === "number" &&
          !Number.isNaN(data.subscriptionPrice)
        ) {
          setUnitPrice(data.subscriptionPrice);
        }
      } catch {
        // Keep default fallback when settings cannot be fetched.
      }
    };

    void fetchSubscriptionPrice();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleModeClick = (mode: Mode) => {
    if (mode === currentMode) {
      return;
    }

    // Only show confirmation when changing from simulated to live
    if (currentMode === "simulated" && mode === "live") {
      setSelectedMode(mode);
      setShowModal(true);
      if (unitsCount < 2) {
        setError(
          locale === "es"
            ? "Deben existir al menos 2 unidades en el edificio para activar el modo en vivo."
            : "At least 2 units must exist in the building to activate live mode."
        );
      } else {
        setError(null);
      }
    } else {
      // Direct change for live to simulated (though this shouldn't happen in practice)
      handleConfirm(mode);
    }
  };

  const handleConfirm = async (modeToSet?: Mode) => {
    const targetMode = modeToSet || selectedMode;
    if (!targetMode) return;

    setChanging(true);
    setError(null);

    try {
      const data = await fetchWithCredentials(`/manager-api/condos/`, {
        method: "PATCH",
        body: JSON.stringify({
          id: condoId,
          mode: targetMode,
        }),
      });

      // Check if we need to redirect to payment
      if (data.redirectUrl) {
        // Redirect to dodo payment page
        window.location.href = data.redirectUrl;
        return;
      }

      onModeChange(targetMode);
      setShowModal(false);
      setSelectedMode(null);
    } catch (err: any) {
      setError(
        err.message ||
          (locale === "es" ? "Error al cambiar el modo" : "Error changing mode")
      );
    } finally {
      setChanging(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedMode(null);
    setError(null);
  };

  return (
    <>
      <div className="flex items-center gap-3">
        <span
          className={`text-sm text-gray-700 ${
            currentMode === "simulated" ? "font-bold" : "font-medium"
          }`}
        >
          {locale === "es" ? "Simulado" : "Simulated"}
        </span>
        <div className="relative">
          <button
            onClick={() =>
              handleModeClick(
                currentMode === "simulated" ? "live" : "simulated"
              )
            }
            disabled={currentMode === "live"}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              currentMode === "live"
                ? "bg-green-600 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600 cursor-pointer"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                currentMode === "live" ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        <span
          className={`text-sm text-gray-700 ${
            currentMode === "live" ? "font-bold" : "font-medium"
          }`}
        >
          {locale === "es" ? "En Vivo" : "Live"}
        </span>
      </div>

      <Modal
        isOpen={showModal}
        onClose={handleCancel}
        title={locale === "es" ? "Activar Modo En Vivo" : "Activate Live Mode"}
      >
        <div className="space-y-4">
          {selectedMode === "live" && (
            <>
              {mustConnectAssistantNumber && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">{connectNumberMessage}</p>
                </div>
              )}
              {unitsCount < 2 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">
                    {locale === "es"
                      ? "Deben existir al menos 2 unidades en el edificio para activar el modo en vivo."
                      : "At least 2 units must exist in the building to activate live mode."}
                  </p>
                </div>
              )}
              {!mustConnectAssistantNumber && unitsCount >= 2 && (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-amber-600 mt-0.5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-amber-700 space-y-2">
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>
                              {locale === "es" ? (
                                <>
                                  Todas las conversaciones existentes serán{" "}
                                  <strong>eliminadas</strong>
                                </>
                              ) : (
                                <>
                                  All existing conversations will be{" "}
                                  <strong>deleted</strong>
                                </>
                              )}
                            </li>
                            <li>
                              {locale === "es"
                                ? "La información aprendida se mantendrá"
                                : "Learned information will be retained"}
                            </li>
                            <li>
                              {locale === "es"
                                ? "Eloria comenzará a responder automáticamente a mensajes reales"
                                : "Eloria will start responding automatically to real messages"}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-blue-700">
                          <p className="font-semibold mb-1">
                            {locale === "es" ? "Cargo Mensual" : "Monthly charge"}
                          </p>
                          <p>
                            {locale === "es" ? (
                              <>
                                Al activar el modo en vivo, se aplicará un cargo
                                mensual de{" "}
                                <strong>
                                  ${totalMonthlyCharge.toLocaleString()} USD
                                </strong>{" "}
                                ({unitsCount}{" "}
                                {unitsCount === 1 ? "unidad" : "unidades"} × $
                                {unitPrice.toFixed(2)} USD/unidad).
                              </>
                            ) : (
                              <>
                                When activating live mode, a monthly charge of{" "}
                                <strong>
                                  ${totalMonthlyCharge.toLocaleString()} USD
                                </strong>{" "}
                                will apply ({unitsCount}{" "}
                                {unitsCount === 1 ? "unit" : "units"} × $
                                {unitPrice.toFixed(2)} USD/unit).
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}
                </>
              )}

              <div className="flex gap-3 justify-end pt-2">
                <Button
                  onClick={handleCancel}
                  variant="secondary"
                  disabled={changing}
                >
                  {locale === "es" ? "Cancelar" : "Cancel"}
                </Button>
                {mustConnectAssistantNumber && (
                  <Link
                    to="/settings"
                    className="inline-flex items-center justify-center rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                  >
                    {connectNumberAction}
                  </Link>
                )}
                {!mustConnectAssistantNumber && unitsCount >= 2 && (
                  <Button
                    onClick={() => handleConfirm()}
                    disabled={changing}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {changing
                      ? locale === "es"
                        ? "Activando..."
                        : "Activating..."
                      : locale === "es"
                        ? "Activar Modo En Vivo"
                        : "Activate Live Mode"}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
